import React, { useState } from "react";
import { useMutation } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { NOTIFY_CUSTOMER_SHIPMENT_MUTATION } from "../../../constants/graphql/mutations";
import { GET_SHIPMENTS_QUERY } from "../../../constants/graphql/queries";
import { useSnackbar } from "notistack";
import PdfGenerateLink from "../../PdfGenerateLink";

const styles = {
  closeButton: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  subtitle: {
    mb: 2,
    textAlign: "center",
    fontSize: "16px",
    color: "text.primary",
    fontWeight: 400,
    lineHeight: 1.5,
  },
};

function NotifyCustomerModal({ shipment, onClose, variables }) {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [notifyCustomer] = useMutation(NOTIFY_CUSTOMER_SHIPMENT_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENTS_QUERY, variables }],
  });

  const handleNotify = async () => {
    setLoading(true);
    try {
      const result = await notifyCustomer({
        variables: { shipmentId: shipment.id },
      });

      if (result.data?.notifyCustomerShipment?.errors?.length > 0) {
        const errorMessage = result.data.notifyCustomerShipment.errors[0].message;
        enqueueSnackbar(errorMessage, { variant: "error" });
      } else {
        enqueueSnackbar("Customer notification sent successfully!", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to send customer notification", { variant: "error" });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box position="relative">
          <Typography fontSize={22} fontWeight={500} color="text.secondary" align="center">
            Approve and Notify Customer
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={2}>
          <Typography sx={styles.subtitle}>
            You're about to notify your customer that shipment{" "}
            <PdfGenerateLink
              resourceType="shipment_pdfs"
              resourceId={shipment?.id}
              label={shipment.shipmentId}
              sx={{
                color: "primary.main",
                textDecoration: "underline",
                fontWeight: 600,
              }}
            />{" "}
            has been received.
            Please review the linked PDF to confirm all details are correct.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleNotify}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Approve and Notify
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NotifyCustomerModal;
